import { ENV } from '@/common/config';
import * as VSensors from '@svg/sensors';

interface SensorsType {
  login: (userId: string) => void;
}
interface VSensorsProp {
  track(event: string, options?: any): void;
  sensors: SensorsType;
}
export let vSensors: VSensorsProp;

/** 缓存的 userId，reinitSensors() 无参调用时复用 */
let cachedUserId: string | undefined;

/** 埋点日志开关，挂载在 window.log 便于控制台调试 */
export let log = false;
/**
 * 非生产环境自定义埋点日志白名单，生产环境不会读取它。
 */
const TRACK_LOG_WHITELIST = ['finished_material_badcase'] as const;
/** 批量发送配置，挂载在 window.batchSend。默认为下方配置，设为 false 可关闭 */
export const defaultBatchSend = {
  datasend_timeout: 6000,
  send_interval: 6000,
  storage_length: 200,
};
export let batchSend: typeof defaultBatchSend | false = defaultBatchSend;
if (typeof window !== 'undefined') {
  (window as any).log = log;
  (window as any).batchSend = batchSend;
}

const isLegacyTrackLogEnabled = () =>
  typeof window !== 'undefined' &&
  (window.location.href.indexOf('log') !== -1 || Boolean((window as any).log));

const shouldPrintTrackLog = (event: string) => {
  if (ENV !== 'prod') {
    // 非生产环境仅输出白名单事件的可读参数日志。
    return TRACK_LOG_WHITELIST.includes(
      event as (typeof TRACK_LOG_WHITELIST)[number],
    );
  }

  // 生产环境保持原规则：URL 含 log 或 window.log 为 true 时全量打印。
  return isLegacyTrackLogEnabled();
};

/**
 * @description 获取用户信息并且初始化埋点
 * @param forceReinit 强制重新初始化，用于 runtime 修改 window.batchSend 后生效
 */
export function initSensor(userId?: string, forceReinit?: boolean) {
  if (forceReinit) {
    (vSensors as any) = undefined;
  }
  if (vSensors) return;

  const loggerInstance = new VSensors.Logger(
    ENV,
    {
      // 关闭神策 SDK 自带的控制台输出，只保留下方统一格式的自定义日志。
      show_log: false,
      isAutoTrack: false,
      heatmap: {
        clickmap: 'not_collect',
        scroll_notice_map: 'not_collect',
      },
      batch_send:
        typeof window !== 'undefined' && (window as any).batchSend === false
          ? false
          : (window as any).batchSend ?? batchSend ?? defaultBatchSend,
      // 多实例
      isMa: true,
    },
    (sensors: any) => {
      sensors.registerPage({
        product_name: 'official_website',
      });
    },
  );

  vSensors = {
    ...loggerInstance,
    track(event: string, options?: any) {
      // 只输出统一格式的自定义日志，不使用神策 SDK 自带的控制台输出。
      if (shouldPrintTrackLog(event)) {
        console.groupCollapsed(`[埋点] ${event}`);
        if (
          options != null &&
          typeof options === 'object' &&
          Object.keys(options).length > 0
        ) {
          console.log('参数:', options);
        } else {
          console.log('参数: 无');
        }
        console.groupEnd();
      }
      // 如果当前路由包含 /mall，则不执行埋点
      if (window.location.pathname.includes('/mall')) {
        return;
      }
      if (typeof loggerInstance.track === 'function') {
        loggerInstance.track(event, options);
      }
    },
    sensors: loggerInstance.sensors,
  };

  if (userId) {
    cachedUserId = userId;
    vSensors.sensors.login(userId);
  }
}

/**
 * 重新初始化埋点。修改 window.batchSend 后调用，使新配置生效。
 * 无参时会复用上次 initSensor 传入的 userId。可控制台使用：window.reinitSensors()
 */
export function reinitSensors(userId?: string) {
  initSensor(userId ?? cachedUserId, true);
}
if (typeof window !== 'undefined') {
  (window as any).reinitSensors = reinitSensors;
}
